import { LocalDate, LocalDateTime } from "@js-joda/core";
import * as messages from "../../messages";
import { AddressComponents } from "../../messages";
import { AgencyId, PatientId } from "../../messages/ObjectIds";
import { VitalMethodType, VitalType, VitalUnitType } from "../../messages/Vital";
import { fmap } from "../../utils/fmap";
import { KeyedList } from "../../utils/List";

export interface AppPatientsState {
  readonly patients: KeyedList<Patient, "id">;
}

export interface Patient {
  id: PatientId;
  agencyId: AgencyId;
  initials: string;
  firstName: string;
  lastName: string;
  fullName: string;
  ssn: string | null;
  gender: string | null;
  address: string | null;
  address2: string | null;
  addressComponents: AddressComponents | null;
  dateOfBirth: LocalDate | null;
  homePhoneNumber: string | null;
  mobilePhoneNumber: string | null;
  phone3: string | null;
  phone4: string | null;
  generalNotes: string | null;
  medicalNotes: string | null;
  priorityCode: number | null;
  mobilityStatus: string | null;
  evacuationLocation: string | null;
  evacuationZone: string | null;
  electricEquipmentDependency: string | null;
  latestVitals: PatientVitals;
  emergencyContact: messages.PatientEmergencyContact[] | null;
  advancedDirectives: messages.PatientAdvancedDirective | null;
  medications: messages.PatientMedicationProfileWithDetails[] | null;
  allergies: messages.Allergy[] | null;
}

export type PatientVitals = {
  [vital in keyof typeof VitalType]: PatientVital | null;
};

export interface PatientVital {
  readonly type: keyof typeof VitalType;
  answer: string | string[] | null;
  measuredAt: LocalDateTime | null;
  method: VitalMethodType | null;
  comments: string | null;
  unit: VitalUnitType | null;
}

export class AppPatientsState {
  static fromMessage(payload: messages.CaregiverPatientsResponse): AppPatientsState {
    const patients = KeyedList.from<Patient>(
      payload.patients.map(AppPatientsState.parseMessageToPatient)
    )("id");

    return { patients: patients };
  }

  static parseMessageToPatient(msg: messages.CaregiverPatientResponse): Patient {
    return {
      id: msg.id,
      agencyId: msg.agencyId,
      initials: `${msg.firstName[0]}${msg.lastName[0]}`,
      firstName: msg.firstName,
      lastName: msg.lastName,
      fullName: `${msg.firstName} ${msg.lastName}`,
      ssn: msg.ssn,
      gender: msg.gender,
      address: msg.address,
      address2: msg.address2,
      addressComponents: msg.addressComponents,
      dateOfBirth: fmap(msg.dateOfBirth, (x) => LocalDate.from(x)),
      homePhoneNumber: msg.homePhoneNumber,
      mobilePhoneNumber: msg.mobilePhoneNumber,
      phone3: msg.phone3,
      phone4: msg.phone4,
      generalNotes: msg.generalNotes,
      medicalNotes: msg.medicalNotes,
      priorityCode: msg.priorityCode,
      mobilityStatus: msg.mobilityStatus,
      evacuationLocation: msg.evacuationLocation,
      evacuationZone: msg.evacuationZone,
      electricEquipmentDependency: msg.electricEquipmentDependency,
      latestVitals: {
        pulseRate: fmap(msg.latestVitals.pulseRate, (x) => ({
          type: "pulseRate",
          ...x,
        })),
        bloodPressure: fmap(msg.latestVitals.bloodPressure, (x) => ({
          type: "bloodPressure",
          ...x,
        })),
        respirationRate: fmap(msg.latestVitals.respirationRate, (x) => ({
          type: "respirationRate",
          ...x,
        })),
        bodyTemperature: fmap(msg.latestVitals.bodyTemperature, (x) => ({
          type: "bodyTemperature",
          ...x,
        })),
      },
      emergencyContact: msg.emergencyContact,
      advancedDirectives: msg.advancedDirectives,
      medications: msg.medications,
      allergies: msg.allergies,
    };
  }
}
